/**
 * Front Office API client — thin axios wrapper.
 * All endpoints under /api/fo. Bookmarkable URL grammar per ADR-FO-1.
 */
import api from "@/lib/api";

export const foApi = {
  dashboard: (params = {}) => api.get("/fo/dashboard", { params }).then(r => r.data),
  todaysWork: (params = {}) => api.get("/fo/todays-work", { params }).then(r => r.data),
  alerts: (params = {}) => api.get("/fo/alerts", { params }).then(r => r.data),
  search: (q, params = {}) => api.get("/fo/search", { params: { q, ...params } }).then(r => r.data),

  workspace: (guestId) => api.get(`/fo/guests/${guestId}/workspace`).then(r => r.data),
  timeline: (guestId, params = {}) => api.get(`/fo/guests/${guestId}/timeline`, { params }).then(r => r.data),

  rooms: (params = {}) => api.get("/fo/rooms", { params }).then(r => r.data),
  roomChangeReasons: () => api.get("/fo/room-change-reasons").then(r => r.data),

  walkIn: (body) => api.post("/fo/walk-in", body).then(r => r.data),
  checkIn: (body) => api.post("/fo/checkin", body).then(r => r.data),
  changeRoom: (stayId, body) => api.post(`/fo/stays/${stayId}/change-room`, body).then(r => r.data),
  extendStay: (stayId, body) => api.post(`/fo/stays/${stayId}/extend`, body).then(r => r.data),

  quickCharge: (body) => api.post("/fo/quick-charge", body).then(r => r.data),
  quickPayment: (body) => api.post("/fo/quick-payment", body).then(r => r.data),

  createComm: (body) => api.post("/fo/communication-log", body).then(r => r.data),
  listComm: (params = {}) => api.get("/fo/communication-log", { params }).then(r => r.data),
  resolveComm: (logId) => api.post(`/fo/communication-log/${logId}/resolve`).then(r => r.data),

  openShift: (body) => api.post("/fo/shifts/open", body).then(r => r.data),
  currentShift: () => api.get("/fo/shifts/current").then(r => r.data),
  addMovement: (shiftId, body) => api.post(`/fo/shifts/${shiftId}/movements`, body).then(r => r.data),
  closeShift: (shiftId, body) => api.post(`/fo/shifts/${shiftId}/close`, body).then(r => r.data),
  handoverShift: (shiftId, body) => api.post(`/fo/shifts/${shiftId}/handover`, body).then(r => r.data),
  shiftSummary: (shiftId) => api.get(`/fo/shifts/${shiftId}/summary`).then(r => r.data),
  shiftsList: (params = {}) => api.get("/fo/shifts", { params }).then(r => r.data),
  shiftTeammates: (params = {}) => api.get("/fo/shift-teammates", { params }).then(r => r.data),
  shiftReportUrl: (shiftId) => `${process.env.REACT_APP_BACKEND_URL}/api/fo/shifts/${shiftId}/report.pdf`,

  registrationCardUrl: (stayId) => `${process.env.REACT_APP_BACKEND_URL}/api/fo/stays/${stayId}/registration-card.pdf`,
  arrivalsRegistrationCardsUrl: (hotelId, date) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/fo/registration-cards/arrivals.pdf?hotel_id=${hotelId}${date ? `&date=${date}` : ""}`,

  regCardSettings: (hotelId) =>
    api.get("/fo/settings/reg-card", { params: { hotel_id: hotelId } }).then(r => r.data),
  saveRegCardSettings: (hotelId, terms) =>
    api.put("/fo/settings/reg-card", { terms }, { params: { hotel_id: hotelId } }).then(r => r.data),
};

export function money(minor) {
  const v = Math.round(Number(minor || 0));
  return `₹${(v / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Extract a human-readable error message from an axios error.
 * FastAPI 422 returns `detail` as an array of Pydantic ValidationError objects;
 * naively passing it to sonner crashes React with "Objects are not valid as a React child".
 */
export function formatApiError(err, fallback = "Something went wrong") {
  const data = err?.response?.data;
  const detail = data?.detail ?? (data && typeof data === "object" && data.code && data.message ? data : undefined);
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => {
      if (typeof d === "string") return d;
      if (d?.msg) {
        const loc = Array.isArray(d.loc) ? d.loc.filter(x => x !== "body").join(".") : "";
        return loc ? `${loc}: ${d.msg}` : d.msg;
      }
      return JSON.stringify(d);
    }).join("; ");
  }
  if (typeof detail === "object") return detail.msg || detail.message || detail.reason || JSON.stringify(detail);
  return String(detail);
}
